import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import {
	isTitleSearchRangeValid,
	useSalesLiveCoachingConversationsQuery,
} from '../../../queries/AIConversation/useSalesLiveCoachingConversationsQuery';
import { Button } from '../../../web/components-v2/Button';
import { Pill } from '../../../web/components-v2/Pill';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { UI_VERSION_FOCUSED } from '../../focused-view/useFocusedViewEnabled';
import {
	formatAgentTemplate,
	formatCallDuration,
	formatCallStatus,
	formatCallTimestamp,
	formatUiVersion,
	isCallAnswered,
} from './callDisplay';
import { getCreatorLabel } from './creatorLabel';
import { getLeadLabel } from './leadLabel';
import { ListFilters } from './ListFilters';
import { styleSheet } from './styles';

interface IProps {
	selectedConversationId: string | null;
	onSelect: (id: string) => void;
}

/**
 * Left-panel list of an account's `SalesLiveCoaching` AI conversations, sorted newest-first.
 * Selecting a row invokes `onSelect` with the conversation id; the selected row is highlighted.
 *
 * Loads one page (max 25) at a time from the cursor-paginated query and appends the next page when
 * the bottom `Waypoint` scrolls into view (auto infinite-scroll), with a "Load more" button as a
 * manual fallback. The search / rep / agent / date-range filters are applied SERVER-SIDE (sent as
 * filter criteria), so they narrow the whole account's conversations — not just the loaded pages —
 * and changing a filter re-queries from the first page. Title search matches persisted titles only.
 *
 * Infinite-scroll loop safety: the `Waypoint` is rendered whenever `hasNextPage` is true (NOT gated
 * on `isFetchingNextPage`), so it stays mounted across fetches and never remounts mid-pagination —
 * `react-waypoint` only fires `onEnter` on the transition into view, so it can't auto-fire in a loop
 * even if a page of rows doesn't overflow the scroll container. The `onEnter` handler also re-checks
 * `hasNextPage && !isFetchingNextPage` before fetching as belt-and-suspenders.
 *
 * Loading uses the shared `LoadingSpinner`; query errors surface via the hook's `meta` toast, so we
 * render an empty body rather than a broken list when data is unavailable. The `filter/account`
 * route is account-admin only, so a non-admin caller gets a 403 — we show a dedicated message.
 */
export function ConversationList({ selectedConversationId, onSelect }: IProps) {
	// Filter state — every filter here is applied SERVER-SIDE (sent as filter criteria).
	const [search, setSearch] = React.useState('');
	const [creatorId, setCreatorId] = React.useState(''); // '' = all reps
	const [agentTemplateId, setAgentTemplateId] = React.useState(''); // '' = every agent
	// Inclusive date bounds as `YYYY-MM-DD` strings ('' = unbounded) — the shape `DateInput` uses.
	const [startDate, setStartDate] = React.useState('');
	const [endDate, setEndDate] = React.useState('');

	// Debounce the title search so typing doesn't re-query on every keystroke.
	const debouncedSearch = useDebounceValue(search, 400);

	// Server-side title search is gated behind a ≤30-day date range (it 400s otherwise), so the search
	// box is only usable once such a range is set.
	const titleSearchEnabled = isTitleSearchRangeValid(startDate, endDate);

	const hasActiveFilters =
		search.trim() !== '' || creatorId !== '' || agentTemplateId !== '' || startDate !== '' || endDate !== '';

	const { data, isLoading, isError, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
		useSalesLiveCoachingConversationsQuery({
			creatorId: creatorId || undefined,
			agentTemplateId: agentTemplateId || undefined,
			startDate: startDate || undefined,
			endDate: endDate || undefined,
			titleSearch: debouncedSearch || undefined,
		});

	// Conversations for the active filter (flattened across pages, already sorted newest-first server-side).
	const conversations = data?.conversations ?? [];
	// Per-conversation call view (conversation + joined phone call + company/contacts), keyed by id.
	const callViewsByConversationId = data?.callViewsByConversationId;
	// Total matching the active filter across ALL pages on the server (null until the first page resolves).
	const totalCount = data?.totalCount ?? null;

	if (isLoading) {
		return (
			<div className={css(styleSheet.listBody)}>
				<div className={css(styleSheet.spinnerState)}>
					<LoadingSpinner type='large' />
				</div>
			</div>
		);
	}

	// `filter/account` is account-admin only; the `/ai-coaching-review` route is admin-gated, but
	// surface a stricter server-side 403 clearly rather than as a confusing empty list.
	const isForbidden = (error as { systemCode?: number } | null | undefined)?.systemCode === 403;
	if (isForbidden) {
		return (
			<div className={css(styleSheet.listState)}>
				You don&rsquo;t have permission to view account-wide coaching conversations.
			</div>
		);
	}

	// A query error (surfaced via the hook's `meta` toast) or a genuinely empty account (no filters,
	// nothing to show) → bare state, no filter bar (filtering an empty account is meaningless). When
	// filters ARE active we keep the bar mounted below even with zero matches, so they can be cleared.
	if (isError || (!hasActiveFilters && conversations.length === 0)) {
		return <div className={css(styleSheet.listState)}>No coaching conversations for this account</div>;
	}

	const loadedCount = conversations.length;
	const countLabel = totalCount != null ? `Showing ${loadedCount} of ${totalCount}` : `Showing ${loadedCount}`;

	return (
		<>
			<ListFilters
				search={search}
				onSearchChange={setSearch}
				titleSearchEnabled={titleSearchEnabled}
				creatorId={creatorId}
				agentTemplateId={agentTemplateId}
				onAgentTemplateChange={setAgentTemplateId}
				onCreatorChange={setCreatorId}
				startDate={startDate}
				onStartDateChange={setStartDate}
				endDate={endDate}
				onEndDateChange={setEndDate}
			/>
			<div className={css(styleSheet.listBody)}>
				{conversations.length === 0 ? (
					<div className={css(styleSheet.listState)}>No conversations match your filters</div>
				) : (
					conversations.map(conversation => {
						const isSelected = conversation.id === selectedConversationId;
						// The phone call is joined onto each conversation by the list query, so scanning info
						// (outcome + which UI the rep was on) is available per row without an extra request.
						const callView = callViewsByConversationId?.get(conversation.id);
						const phoneCall = callView?.phoneCall;
						const answered = isCallAnswered(phoneCall);
						// Connected talk time (m:ss), shown right-aligned so longer calls are easy to scan for.
						const durationLabel = formatCallDuration(phoneCall?.duration);
						// Who the call was with: the lead(s) and their company, for at-a-glance scanning.
						const companyName = callView?.company?.companyName?.trim();
						const leadLabel = getLeadLabel(callView?.contacts);
						// From the conversation, not the call — a conversation with no linked call still ran on a
						// template, and both halves carry the same pair.
						const agentTemplate = formatAgentTemplate(conversation);

						// Rows lead with the rep name + date. We intentionally do NOT render the conversation
						// `title`: for SalesLiveCoaching it is the rep's full opening prompt, which already shows
						// in the transcript on the right — duplicating it here made every row a wall of identical
						// boilerplate.
						return (
							<Button
								key={conversation.id}
								variant='ghost'
								onClick={() => onSelect(conversation.id)}
								styles={[styleSheet.listItem, ...(isSelected ? [styleSheet.listItemSelected] : [])]}
							>
								<div className={css(styleSheet.listItemTitle)}>{getCreatorLabel(conversation.creator)}</div>
								{leadLabel || companyName ? (
									<div className={css(styleSheet.listItemSubject)}>
										{leadLabel ? <span className={css(styleSheet.listItemLead)}>{leadLabel}</span> : null}
										{leadLabel && companyName ? (
											<span className={css(styleSheet.listItemSubjectSep)} aria-hidden='true'>
												·
											</span>
										) : null}
										{companyName ? <span className={css(styleSheet.listItemCompany)}>{companyName}</span> : null}
									</div>
								) : null}
								<div className={css(styleSheet.listItemMeta)}>
									<span>{formatCallTimestamp(conversation.creationDate)}</span>
									{durationLabel ? (
										<span className={css(styleSheet.listItemDuration)} title='Connected talk time'>
											{durationLabel}
										</span>
									) : null}
								</div>
								{phoneCall || agentTemplate ? (
									<div className={css(styleSheet.listItemTags)}>
										{phoneCall ? (
											<>
												{/* Outcome: a connected call shows its status (green); an unconnected one reads "No answer" (gray). */}
												<Pill
													variant='tag'
													size='small'
													color={answered ? 'green' : 'gray'}
													label={answered ? formatCallStatus(phoneCall.callStatus) : 'No answer'}
												/>
												{phoneCall.uiVersion ? (
													<Pill
														variant='tag'
														size='small'
														color={phoneCall.uiVersion === UI_VERSION_FOCUSED ? 'blue' : 'gray'}
														label={formatUiVersion(phoneCall.uiVersion)}
													/>
												) : null}
											</>
										) : null}
										{agentTemplate ? (
											<span title={agentTemplate.title}>
												<Pill variant='tag' size='small' color='purple' label={agentTemplate.label} />
											</span>
										) : null}
									</div>
								) : null}
							</Button>
						);
					})
				)}

				{/* Auto-load the next page when this sentinel scrolls into view. Kept mounted while
				    `hasNextPage` (independent of `isFetchingNextPage`) so it never remounts mid-pagination
				    and cannot auto-fire in a loop — see the loop-safety note in the component doc. */}
				{hasNextPage ? (
					<Waypoint
						key='coaching-conversations-waypoint'
						bottomOffset='-200px'
						onEnter={() => {
							if (hasNextPage && !isFetchingNextPage) {
								fetchNextPage();
							}
						}}
					/>
				) : null}

				<div className={css(styleSheet.listFooter)}>
					{hasNextPage ? (
						<Button
							variant='secondary'
							onClick={() => fetchNextPage()}
							disabled={isFetchingNextPage}
							styles={[styleSheet.loadMoreButton]}
						>
							{isFetchingNextPage ? 'Loading…' : 'Load more'}
						</Button>
					) : null}
					<span className={css(styleSheet.listCount)}>{countLabel}</span>
				</div>
			</div>
		</>
	);
}
